import { useCallback, useEffect, useRef, useState } from 'react'
import { constants } from 'apps'

const VIEWFI_SDK_URL = process.env.REACT_APP_VIEWFI_SDK_URL ?? 'https://viewfi.live/viewfi-sdk-widget.js'

/** Remove ViewFi SDK overlay and popup elements from the DOM */
export function cleanupViewFiElements() {
  try {
    window.ViewFiSDK?.closePopup()
  } catch (_) { /* ignore */ }

  document.getElementById('viewfi-overlay')?.remove()
  document.getElementById('viewfi-popup')?.remove()
}

export interface RemainingViewsResult {
  remainingViews: number
  dailyLimit: number
  adViewsToday: number
  loggedIn: boolean
}

export interface UseViewFiSDKOptions {
  /** Whether to load the SDK script. When false, script is not loaded and events are ignored. */
  enabled: boolean
  /** Called when adCompleted event fires — ad fully completed and reward ready to claim. */
  onAdCompleted?: () => void
  /** Called when reward-pending event fires — reward is being processed (intermediate state). */
  onRewardPending?: () => void
  /** Called when an error occurs during ad playback or reward credit. */
  onAdError?: (e: Event) => void
  /** Called when reward delivery is rejected by the server. */
  onRewardRejected?: (e: Event) => void
  /** Called when the daily ad view limit has been reached. */
  onDailyLimitReached?: (e: Event) => void
  /** Called when the SDK script fails to load. */
  onScriptError?: () => void
  /** Max time (ms) to wait for window.ViewFiSDK after script loads. Default: 3000. */
  pollTimeout?: number
}

export interface UseViewFiSDKReturn {
  /** True while waiting for the SDK reward credit after video ends. */
  isProcessing: boolean
  /** Processing message text — changes when reward-pending fires. */
  processingMessage: string
  /** True once the SDK script has loaded and window.ViewFiSDK is available. */
  isSdkReady: boolean
  /** Fetch remaining views from the SDK. Returns null if SDK not ready or on error/timeout. */
  getRemainingViews: () => Promise<RemainingViewsResult | null>
}

/**
 * Custom hook that encapsulates the entire ViewFi SDK lifecycle:
 * - Script loading / cleanup
 * - DOM event subscription (adVideoEnded, adCompleted, adError)
 * - "Processing…" state management
 * - CSS injection to hide SDK prompt & overlay during processing
 *
 * Consumers only need to provide callbacks for `onRewardPending` and `onAdError`.
 */
export default function useViewFiSDK({
  enabled,
  onAdCompleted,
  onRewardPending,
  onAdError,
  onRewardRejected,
  onDailyLimitReached,
  onScriptError,
  pollTimeout = 3000,
}: UseViewFiSDKOptions): UseViewFiSDKReturn {
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingMessage, setProcessingMessage] = useState('Processing...')
  const [isSdkReady, setIsSdkReady] = useState(false)
  const completedRef = useRef(false)

  // Keep callbacks in refs to avoid re-subscribing events on every render
  const onAdCompletedRef = useRef(onAdCompleted)
  const onRewardPendingRef = useRef(onRewardPending)
  const onAdErrorRef = useRef(onAdError)
  const onRewardRejectedRef = useRef(onRewardRejected)
  const onDailyLimitReachedRef = useRef(onDailyLimitReached)
  const onScriptErrorRef = useRef(onScriptError)

  useEffect(() => {
    onAdCompletedRef.current = onAdCompleted
    onRewardPendingRef.current = onRewardPending
    onAdErrorRef.current = onAdError
    onRewardRejectedRef.current = onRewardRejected
    onDailyLimitReachedRef.current = onDailyLimitReached
    onScriptErrorRef.current = onScriptError
  }, [onAdCompleted, onRewardPending, onAdError, onRewardRejected, onDailyLimitReached, onScriptError])

  // ── Load SDK script ──────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) {
      setIsSdkReady(false)
      return
    }

    // Reset completed guard for each new SDK session
    completedRef.current = false

    // Force re-initialization by removing old script if any
    const existingScript = document.querySelector(`script[src="${VIEWFI_SDK_URL}"]`)
    if (existingScript) {
      existingScript.remove()
    }

    let pollInterval: NodeJS.Timeout | undefined

    const script = document.createElement('script')
    script.src = VIEWFI_SDK_URL
    script.async = true
    script.onload = () => {
      // Poll for SDK availability (script may init asynchronously)
      let attempts = 0
      const maxAttempts = Math.ceil(pollTimeout / 100)
      pollInterval = setInterval(() => {
        attempts++
        if (window.ViewFiSDK) {
          if (pollInterval) clearInterval(pollInterval)
          pollInterval = undefined
          setIsSdkReady(true)
        } else if (attempts >= maxAttempts) {
          if (pollInterval) clearInterval(pollInterval)
          pollInterval = undefined
          onScriptErrorRef.current?.()
        }
      }, 100)
    }
    script.onerror = () => {
      onScriptErrorRef.current?.()
    }
    document.body.appendChild(script)

    return () => {
      if (pollInterval) clearInterval(pollInterval)
      setIsSdkReady(false)
      cleanupViewFiElements()

      const s = document.querySelector(`script[src="${VIEWFI_SDK_URL}"]`)
      if (s) {
        s.remove()
      }
    }
  }, [enabled])

  // ── Subscribe to ViewFi SDK DOM CustomEvents ─────────────────────────
  // adStarted      — video begins playing
  // adVideoEnded   — video finishes, show "Processing…" state
  // reward-pending — intermediate: reward is being processed, update message
  // adCompleted    — success: reward credited, triggers completed callback
  // adError        — error during ad playback or reward credit
  // reward-rejected — reward delivery rejected by server
  useEffect(() => {
    if (!enabled) return

    let timeoutId: NodeJS.Timeout

    const handleAdStarted = () => {
      completedRef.current = false
    }

    const handleVideoEnded = () => {
      if (completedRef.current) return

      const sdkUser = localStorage.getItem(constants.VIEWFI_SDK_USER_KEY)
      if (sdkUser) {
        setIsProcessing(true)

        // Timeout 180s: if no adCompleted or adError event is received, bypass
        timeoutId = setTimeout(() => {
          if (completedRef.current) return
          cleanupViewFiElements()
          setIsProcessing(false)
          onAdErrorRef.current?.(new CustomEvent('viewfi:timeout'))
        }, 180000)
      }
    }

    const handleCompleted = () => {
      if (timeoutId) clearTimeout(timeoutId)
      if (completedRef.current) return
      completedRef.current = true

      cleanupViewFiElements()
      setIsProcessing(false)
      setProcessingMessage('Processing...')
      onAdCompletedRef.current?.()
    }

    const handleError = (e: Event) => {
      if (timeoutId) clearTimeout(timeoutId)
      cleanupViewFiElements()
      setIsProcessing(false)
      setProcessingMessage('Processing...')
      onAdErrorRef.current?.(e)
    }

    const handleRewardPending = () => {
      if (completedRef.current) return
      setProcessingMessage('Rewarding in progress...')
      onRewardPendingRef.current?.()
    }

    const handleRewardRejected = (e: Event) => {
      if (timeoutId) clearTimeout(timeoutId)
      cleanupViewFiElements()
      setIsProcessing(false)
      setProcessingMessage('Processing...')
      onRewardRejectedRef.current?.(e)
    }

    // const handleDailyLimitReached = (e: Event) => {
    //   if (timeoutId) clearTimeout(timeoutId)
    //   cleanupViewFiElements()
    //   setIsProcessing(false)
    //   onDailyLimitReachedRef.current?.(e)
    // }

    document.addEventListener('viewfi:adStarted', handleAdStarted)
    document.addEventListener('viewfi:adVideoEnded', handleVideoEnded)
    document.addEventListener('viewfi:adCompleted', handleCompleted)
    document.addEventListener('viewfi:adError', handleError)
    document.addEventListener('viewfi:reward-pending', handleRewardPending)
    document.addEventListener('viewfi:reward-rejected', handleRewardRejected)
    // document.addEventListener('viewfi:daily-limit-reached', handleDailyLimitReached)

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      document.removeEventListener('viewfi:adStarted', handleAdStarted)
      document.removeEventListener('viewfi:adVideoEnded', handleVideoEnded)
      document.removeEventListener('viewfi:adCompleted', handleCompleted)
      document.removeEventListener('viewfi:adError', handleError)
      document.removeEventListener('viewfi:reward-pending', handleRewardPending)
      document.removeEventListener('viewfi:reward-rejected', handleRewardRejected)
      // document.removeEventListener('viewfi:daily-limit-reached', handleDailyLimitReached)
    }
  }, [enabled])

  // ── Hide the floating prompt injected by ViewFi SDK ──────────────────
  // The SDK re-creates #viewfi-press-prompt every 5s via setInterval.
  // Inject a CSS rule to hide it permanently — the SDK thinks it's still
  // visible so it won't re-create it.
  useEffect(() => {
    const styleId = 'viewfi-hide-prompt'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        #viewfi-press-prompt {
          display: none !important;
        }
      `
      document.head.appendChild(style)
    }
    // NOTE: intentionally no cleanup — the SDK's setInterval keeps running
    // even after the script tag is removed, so the CSS must persist globally.
  }, [])

  // ── Hide ViewFi post-ad popups ONLY when processing reward ───────────
  useEffect(() => {
    const processingStyleId = 'viewfi-processing-hide'
    if (isProcessing) {
      if (!document.getElementById(processingStyleId)) {
        const style = document.createElement('style')
        style.id = processingStyleId
        style.textContent = `
          #viewfi-overlay,
          #viewfi-popup {
            display: none !important;
          }
        `
        document.head.appendChild(style)
      }
    } else {
      document.getElementById(processingStyleId)?.remove()
    }
    return () => {
      document.getElementById(processingStyleId)?.remove()
    }
  }, [isProcessing])

  // ── getRemainingViews wrapper with timeout ──────────────────────────
  const getRemainingViews = useCallback(async (): Promise<RemainingViewsResult | null> => {
    if (!window.ViewFiSDK) return null

    try {
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('getRemainingViews timeout')), 10000)
      )
      const result = await Promise.race([
        window.ViewFiSDK.getRemainingViews(),
        timeout,
      ])
      return result
    } catch {
      return null
    }
  }, [])

  return { isProcessing, processingMessage, isSdkReady, getRemainingViews }
}
